import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useCreatedResellers, useDeleteReseller } from "@/api/reseller-manager";
import { MeterTable, usePagination } from "@/components/meters/meter-table";
import { CreateResellerDialog } from "@/components/resellers/create-reseller-dialog";
import { DeleteResellerAlertDialog } from "@/components/resellers/delete-reseller-alert-dialog";
import { EditResellerDialog } from "@/components/resellers/edit-reseller-dialog";
import { ResellerRowActions } from "@/components/resellers/reseller-row-actions";
import { ResellerSheet } from "@/components/resellers/reseller-sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useDebounce } from "@/hooks/use-debounce";
import { displayDate } from "@/lib/empty-value";

export function ResellersPage() {
	const { t, i18n } = useTranslation();
	const { page, setPage } = usePagination();
	const [pageSize, setPageSize] = useState(20);
	const [search, setSearch] = useState("");
	const [ordering, setOrdering] = useState("");
	const debouncedSearch = useDebounce(search);
	const { data, isLoading } = useCreatedResellers({
		page,
		page_size: pageSize,
		search: debouncedSearch || undefined,
		ordering: ordering || undefined,
	});
	const [selectedId, setSelectedId] = useState<number | null>(null);
	const [createOpen, setCreateOpen] = useState(false);
	const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);
	const [editTarget, setEditTarget] = useState<{
		id: number;
		email: string;
		company_name: string;
	} | null>(null);
	const deleteMutation = useDeleteReseller();

	function handleDetails(id: number) {
		setSelectedId(id);
	}

	function handleEdit(row: {
		id: number;
		emails: string[];
		company_name: string;
	}) {
		setEditTarget({
			id: row.id,
			email: row.emails[0] ?? "",
			company_name: row.company_name,
		});
	}

	function handleDelete(id: number) {
		deleteMutation.mutate(id, {
			onSuccess: () => toast.success(t("resellers.deleted")),
			onError: () => toast.error(t("resellers.deleteError")),
		});
	}

	const columns = [
		{
			key: "company_name" as const,
			label: t("resellers.colCompanyName"),
			sortField: "company_name",
			render: (row: { company_name: string }) => row.company_name,
		},
		{
			key: "managers_count" as const,
			label: t("resellers.colManagersCount"),
			render: (row: { managers_count: number }) => row.managers_count,
		},
		{
			key: "meters_count" as const,
			label: t("resellers.colMetersCount"),
			render: (row: { meters_count: number }) => row.meters_count,
		},
		{
			key: "is_confirmed" as const,
			label: t("resellers.colConfirmed"),
			render: (row: { is_confirmed: boolean }) => (
				<Badge variant={row.is_confirmed ? "default" : "outline"}>
					{row.is_confirmed
						? t("resellers.statusConfirmed")
						: t("resellers.statusUnconfirmed")}
				</Badge>
			),
		},
		{
			key: "created_at" as const,
			label: t("resellers.colCreatedAt"),
			render: (row: { created_at: string | null }) =>
				displayDate(row.created_at, i18n.language, {
					day: "2-digit",
					month: "2-digit",
					year: "numeric",
					hour: "2-digit",
					minute: "2-digit",
				}),
		},
		{
			key: "_actions" as const,
			label: "",
			render: (row: { id: number; emails: string[]; company_name: string }) => (
				<ResellerRowActions
					onDetails={() => handleDetails(row.id)}
					onEdit={() => handleEdit(row)}
					onDelete={() => handleDelete(row.id)}
					isDeleting={deleteMutation.isPending}
				/>
			),
		},
	];

	return (
		<>
			<div className="flex flex-1 flex-col gap-4 p-4 lg:p-6">
				<MeterTable
					data={data}
					columns={columns}
					loading={isLoading}
					page={page}
					pageSize={pageSize}
					onPageChange={(p) => setPage(p)}
					onPageSizeChange={(s) => {
						setPageSize(s);
						setPage(1);
					}}
					search={search}
					onSearchChange={(s) => {
						setSearch(s);
						setPage(1);
					}}
					ordering={ordering}
					onOrderingChange={(o) => {
						setOrdering(o);
						setPage(1);
					}}
					headerActions={
						<div className="flex justify-end">
							<Button onClick={() => setCreateOpen(true)}>
								{t("resellers.createButton")}
							</Button>
						</div>
					}
				/>
			</div>
			<CreateResellerDialog open={createOpen} onOpenChange={setCreateOpen} />
			<EditResellerDialog
				reseller={editTarget}
				onOpenChange={(o) => !o && setEditTarget(null)}
			/>
			<ResellerSheet
				resellerId={selectedId}
				onOpenChange={(o) => !o && setSelectedId(null)}
				onRequestDelete={(id) => {
					setSelectedId(null);
					setPendingDeleteId(id);
				}}
				deletePending={deleteMutation.isPending}
			/>
			<DeleteResellerAlertDialog
				open={pendingDeleteId !== null}
				onOpenChange={(o) => !o && setPendingDeleteId(null)}
				pending={deleteMutation.isPending}
				onConfirm={() => {
					if (pendingDeleteId !== null) {
						deleteMutation.mutate(pendingDeleteId, {
							onSuccess: () => {
								toast.success(t("resellers.deleted"));
								setPendingDeleteId(null);
							},
							onError: () => toast.error(t("resellers.deleteError")),
						});
					}
				}}
			/>
		</>
	);
}
